<template>
  <div>
    <v-card>
      <v-card-title>
        <v-icon color="primary">mdi-help-circle-outline</v-icon>
        <span class="px-2">{{ $t('driver_faqs') }}</span>
        <v-spacer></v-spacer>
        <create-button @create="showCreateDialog"></create-button>
      </v-card-title>
      <v-data-table
        :no-data-text="$t('noData')"
        :no-results-text="$t('noResults')"
        item-key="id"
        :loading="isLoading"
        :loading-text="$t('loading_text')"
        :headers="translatedHeaders"
        :items="faqs"
        :search="search"
        :footer-props="footerProps"
      >
        <template v-slot:top>
          <v-text-field
            v-model="search"
            :label="$t('search')"
            class="mx-4"
          ></v-text-field>
        </template>
        <template v-slot:item.question="{ item }">
          <div>
            <strong>{{ item.question_ar }}</strong>
            <div v-if="item.question_en" class="text-caption text-muted">{{ item.question_en }}</div>
          </div>
        </template>
        <template v-slot:item.answer="{ item }">
          <div class="text-truncate" style="max-width: 300px;">
            <span>{{ item.answer_ar }}</span>
            <div v-if="item.answer_en" class="text-caption text-muted text-truncate">{{ item.answer_en }}</div>
          </div>
        </template>
        <template v-slot:item.is_active="{ item }">
          <v-chip :color="item.is_active ? 'success' : 'error'" dark small>
            {{ item.is_active ? $t('active') : $t('inactive') }}
          </v-chip>
        </template>
        <template v-slot:item.created_at="{ item }">
          <span>{{ formatDate(item.created_at) }}</span>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon small class="mx-2" @click="editItem(item)">mdi-pencil</v-icon>
          <v-icon small class="mx-2" @click="deleteItem(item, faqs.indexOf(item))">mdi-delete</v-icon>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" persistent max-width="700px">
      <v-form ref="form" v-model="valid" lazy-validation>
        <v-card>
          <v-card-title>
            <span class="text-h5">{{ isEdit ? $t('edit_driver_faq') : $t('add_driver_faq') }}</span>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <!-- Question AR (Required) -->
                <v-col cols="12">
                  <v-text-field
                    v-model="editedItem.question_ar"
                    :rules="[v => !!v || 'Arabic Question is required']"
                    :label="$t('question_ar') + '*'"
                    required
                  ></v-text-field>
                </v-col>

                <!-- Answer AR (Required) -->
                <v-col cols="12">
                  <v-textarea
                    v-model="editedItem.answer_ar"
                    :rules="[v => !!v || 'Arabic Answer is required']"
                    :label="$t('answer_ar') + '*'"
                    required
                    rows="3"
                  ></v-textarea>
                </v-col>

                <!-- Question EN (Optional) -->
                <v-col cols="12">
                  <v-text-field
                    v-model="editedItem.question_en"
                    :label="$t('question_en')"
                  ></v-text-field>
                </v-col>

                <!-- Answer EN (Optional) -->
                <v-col cols="12">
                  <v-textarea
                    v-model="editedItem.answer_en"
                    :label="$t('answer_en')"
                    rows="3"
                  ></v-textarea>
                </v-col>

                <!-- Sort Order -->
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.sort_order"
                    type="number"
                    min="0"
                    :label="$t('sort_order')"
                  ></v-text-field>
                </v-col>

                <!-- Is Active -->
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="editedItem.is_active"
                    :items="activeOptions"
                    item-text="text"
                    item-value="value"
                    :label="$t('is_active')"
                  ></v-select>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="error" text @click="dialog = false">{{ $t('cancel') }}</v-btn>
            <v-btn color="success" text :disabled="!valid || isSaving" @click="saveItem">{{ $t('Save Changes') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>
  </div>
</template>

<script>
import CreateButton from "@/components/CreateButton";

export default {
  components: {
    CreateButton
  },
  data() {
    return {
      faqs: [],
      isLoading: false,
      isSaving: false,
      search: "",
      dialog: false,
      isEdit: false,
      valid: true,
      editedItem: {
        id: null,
        question_ar: "",
        answer_ar: "",
        question_en: "",
        answer_en: "",
        sort_order: 0,
        is_active: true
      },
      headers: [
        { text: "id", value: "id", align: "start" },
        { text: "driver_faqs", value: "question" },
        { text: "answer_ar", value: "answer", sortable: false },
        { text: "sort_order", value: "sort_order" },
        { text: "is_active", value: "is_active" },
        { text: "Created At", value: "created_at" },
        { text: "Actions", value: "actions", sortable: false }
      ]
    };
  },
  computed: {
    translatedHeaders() {
      return this.headers.map(header => ({
        ...header,
        text: this.$t(header.text)
      }));
    },
    footerProps() {
      return {
        itemsPerPageText: this.$t("rows_per_page")
      };
    },
    activeOptions() {
      return [
        { text: this.$t("active"), value: true },
        { text: this.$t("inactive"), value: false }
      ];
    }
  },
  mounted() {
    this.loadFaqs();
  },
  methods: {
    loadFaqs() {
      this.isLoading = true;
      axios
        .get("/driver-faqs")
        .then(response => {
          this.faqs = response.data;
        })
        .catch(error => {
          this.$notify({
            title: this.$t("Error"),
            text: "Failed to load FAQs",
            type: "error"
          });
          console.error(error);
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    showCreateDialog() {
      this.isEdit = false;
      this.editedItem = {
        id: null,
        question_ar: "",
        answer_ar: "",
        question_en: "",
        answer_en: "",
        sort_order: 0,
        is_active: true
      };
      this.dialog = true;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    editItem(item) {
      this.isEdit = true;
      this.editedItem = Object.assign({}, item);
      this.editedItem.is_active = !!item.is_active;
      this.dialog = true;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    deleteItem(item, index) {
      if (confirm("Are you sure you want to delete this FAQ?")) {
        axios
          .delete(`/driver-faqs/${item.id}`)
          .then(() => {
            this.faqs.splice(index, 1);
            this.$notify({
              title: this.$t("Success"),
              text: "FAQ deleted successfully",
              type: "success"
            });
          })
          .catch(error => {
            this.$notify({
              title: this.$t("Error"),
              text: "Failed to delete FAQ",
              type: "error"
            });
            console.error(error);
          });
      }
    },
    saveItem() {
      if (this.$refs.form.validate()) {
        this.isSaving = true;

        const payload = {
          question_ar: this.editedItem.question_ar,
          answer_ar: this.editedItem.answer_ar,
          question_en: this.editedItem.question_en || null,
          answer_en: this.editedItem.answer_en || null,
          is_active: this.editedItem.is_active ? 1 : 0,
          sort_order: this.editedItem.sort_order !== null && this.editedItem.sort_order !== '' ? parseInt(this.editedItem.sort_order) : 0
        };

        const url = this.isEdit ? `/driver-faqs/${this.editedItem.id}` : "/driver-faqs";
        const method = this.isEdit ? "put" : "post";

        axios[method](url, payload)
          .then(() => {
            this.dialog = false;
            this.$notify({
              title: this.$t("Success"),
              text: this.isEdit ? "FAQ updated successfully" : "FAQ created successfully",
              type: "success"
            });
            this.loadFaqs();
          })
          .catch(error => {
            this.$notify({
              title: this.$t("Error"),
              text: "Failed to save FAQ",
              type: "error"
            });
            console.error(error);
          })
          .then(() => {
            this.isSaving = false;
          });
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "-";
      const date = new Date(dateStr);
      return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  }
};
</script>
